import $ from "jquery";
import jsQR from "jsqr";
import bdjs from "@bd-core/bdjs";
import CFG from "@bd-core/config";
import videojs from "video.js";

export const HomePage = () => {
    const elms = {
        homePage: "#home-page",
        app: ".app",
        introVideo: "#intro-video",
        classIntroVideo: ".intro__video",
        classIntroVideoIOS: ".intro__video-ios",
        introVideoNormal: ".intro__video-ios .intro__video-normal",
        introVideoFullscreen: ".intro__video-ios .intro__video-fullscreen",
        introPlayVideo: ".js-play-intro-video",
        introFullscreenVideo: ".js-fullscreen-intro-video",
        introFullscreenText: ".intro__text",
        intro: ".intro",
        skipIntroBtn: ".skip-instruction-btn",
        alertText: ".home-not-found-alert",
        question: ".js-question",
        questionClue: ".question__clue",
        answer: ".js-answer",
        questionVideo: "#question-video",
        questionAudio: "#question-audio",
        classQuestionVideo: ".question__video",
        classQuestionVideoIOS: ".question__video-ios",
        classQuestionAudio: ".question__audio",
        classQuestionImage: ".question__image",
        classQuestionInner: ".question__inner",
        classQuestionFullScreen: ".question__text",
        classAnswerFullScreen: ".answer__text",
        replay: ".js-replay",
        questionAudioPlay: ".js-play-question-audio",
        questionAudioContainer: ".js-question-audio",
        questionAudioText: ".js-question-audio-text",
        showAnswer: ".js-show-answer",
        showAnswerQuestion: ".js-show-answer-question",
        nextQuestion: ".js-next-question",
        fullscreenVideo: ".js-fullscreen-video",
        fullscreenAnswerVideo: ".js-fullscreen-answer-video",
        classAnswerVideo: ".answer__content_video-ios",
        playVideo: ".js-play-video",
        playAnswerVideo: ".js-answer-play-video",
        qrVideo: "#qr-video",
        qrCanvas: "#qr-canvas",
        answerText: ".answer__content_text",
        answerReplay: ".js-answer-replay",
        answerVideoContainer: ".js-answer-video",
        answerVideoContainerIOS: ".answer__content_video-ios",
        answerAudioContainer: ".js-answer-audio",
        answerTitle: ".answer__content_text-title",
        answerYear: ".answer__title",
        answerSubtitle: ".answer__subtitle",
        answerCategory: ".answer__content_subtext",
        answerDescription: ".answer__content_text-text",
        answerBackground: ".answer__background",
        answerAudioText: ".answer__content_audio-text",
        answerVideo: "#answer-video",
        answerAudio: "#answer-audio",
        answerImage: "#answer-image",
        answerImageContainer: ".answer__content_imageContainer",
        answerAudioReplay: ".js-replay-audio",

        questionVideoNormal: ".question__video-normal",
        questionVideoFullscreen: ".question__video-fullscreen",

        answerVideoNormal: ".answer__content_video-normal",
        answerVideoFullscreen: ".answer__content_video-fullscreen",

        questionImageFullscreen: ".js-fullscreen-image",
        questionImageContainer: ".question__imageContainer",

        answerImageFullscreen: ".js-fullscreen-answer-image",

        questionWarning: ".question__warning",
        answerWarning: ".answer__warning",
    };

    const INTRO_WATCHED_SESSION_KEY = "fci_all_stars_intro_watched_session";
    const INTRO_WATCHED_LEGACY_KEY = "fci_all_stars_intro_watched";

    const homeURL =
        window.location.origin +
        window.location.pathname +
        window.location.search;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIPhoneSafari =
        /iPhone/.test(navigator.userAgent) &&
        /Safari/.test(navigator.userAgent) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);

    const isHomePage = $(elms.homePage).length > 0;
    if (!isHomePage) return false;
    let stream = null;
    let scanning = false;
    let scanQRCode = null;

    const isIntroOpen = () => {
        const $intro = $(elms.intro);
        return $intro.length > 0 && $intro.hasClass("is-open");
    };

    const startScannerIfAllowed = () => {
        if (scanning || isIntroOpen()) return;
        initQRScanner();
    };

    /**
     * Force-stop HTML video element.
     * iPhone Safari can keep audio alive after pause, so use a stronger fallback there.
     * @param {HTMLVideoElement | null | undefined} video
     */
    const hardStopHtmlIntroVideo = (video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        try {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
        } catch (e) {
            // ignore
        }
        if (!isIPhoneSafari) return;
        try {
            video.srcObject = null;
            video.removeAttribute("src");
            video.querySelectorAll("source").forEach((s) => {
                s.removeAttribute("src");
            });
            video.load();
        } catch (e2) {
            // ignore
        }
    };

    /**
     * Stop all intro playback paths (Video.js, native intro video, iOS normal/fullscreen).
     * Runs before overlay state checks so skip always kills audio even if `.is-open` is wrong.
     */
    const stopIntroPlayback = () => {
        $(elms.introPlayVideo).removeClass("is-playing");

        const normalVideo = /** @type {HTMLVideoElement} */ (
            $(elms.introVideoNormal)[0]
        );
        const fullscreenVideo = /** @type {HTMLVideoElement} */ (
            $(elms.introVideoFullscreen)[0]
        );
        const videoEl = document.querySelector(elms.introVideo);

        let player = null;
        try {
            player = videoEl
                ? videojs.getPlayer(videoEl)
                : videojs.getPlayer(elms.introVideo);
        } catch (e) {
            player = null;
        }
        if (player) {
            try {
                player.pause();
                player.currentTime(0);
            } catch (e2) {
                // ignore
            }
        }

        hardStopHtmlIntroVideo(
            videoEl instanceof HTMLVideoElement ? videoEl : null,
        );
        hardStopHtmlIntroVideo(normalVideo);
        hardStopHtmlIntroVideo(fullscreenVideo);
    };

    /**
     * Close intro overlay and mark intro as watched in sessionStorage.
     * Idempotent: safe to call from skip button and video ended.
     * sessionStorage survives in-app home reloads (e.g. next question)
     * but is cleared on login so each employee sees the intro.
     */
    const closeIntro = () => {
        stopIntroPlayback();

        const $intro = $(elms.intro);
        if (!$intro.length) return;
        if (!$intro.hasClass("is-open")) return;

        try {
            sessionStorage.setItem(INTRO_WATCHED_SESSION_KEY, "1");
        } catch (e) {
            // ignore storage errors (private mode, etc.)
        }
        $intro.removeClass("is-open");

        startScannerIfAllowed();
    };

    /**
     * Intro video orientation for iOS only.
     * Sets is-portrait / is-landscape on .intro__video-ios from native video dimensions.
     */
    const initIntroVideoIOS = () => {
        const $container = $(elms.classIntroVideoIOS);
        if (!$container.length) return;

        const normalVideo = /** @type {HTMLVideoElement} */ (
            $container.find(".intro__video-normal")[0]
        );
        const fullscreenVideo = /** @type {HTMLVideoElement} */ (
            $container.find(".intro__video-fullscreen")[0]
        );
        if (!normalVideo && !fullscreenVideo) return;

        const setIntroIOSOrientation = (videoEl) => {
            const el = videoEl || normalVideo || fullscreenVideo;
            if (!el || !el.videoWidth || !el.videoHeight) return;

            const isPortrait = el.videoHeight > el.videoWidth;
            const isLandscape = el.videoWidth > el.videoHeight;

            $container.removeClass("is-portrait is-landscape");
            if (isPortrait) $container.addClass("is-portrait");
            if (isLandscape) $container.addClass("is-landscape");
        };

        const bindOrientation = (videoEl) => {
            if (!videoEl) return;
            videoEl.addEventListener("loadedmetadata", () =>
                setIntroIOSOrientation(videoEl),
            );
            videoEl.addEventListener("loadeddata", () =>
                setIntroIOSOrientation(videoEl),
            );
            videoEl.addEventListener("canplay", () =>
                setIntroIOSOrientation(videoEl),
            );
            if (videoEl.readyState >= 1) {
                setIntroIOSOrientation(videoEl);
            }
        };

        bindOrientation(normalVideo);
        bindOrientation(fullscreenVideo);
        // Fallback: try again after a short delay (iOS may report dimensions late)
        setTimeout(() => setIntroIOSOrientation(normalVideo), 500);

        if (normalVideo) {
            normalVideo.addEventListener("ended", closeIntro);
        }
        if (fullscreenVideo) {
            fullscreenVideo.addEventListener("ended", closeIntro);
        }
    };

    /**
     * Intro video: Video.js init and orientation.
     * Matches question video pattern: create player then set source via API so play works.
     */
    const initIntroVideo = () => {
        const videoEl = document.querySelector(elms.introVideo);
        if (!videoEl) return;
        if (videojs.getPlayer(videoEl)) return;

        const sourceEl = videoEl.querySelector("source");
        const existingSrc =
            videoEl.getAttribute("src") ||
            (sourceEl && sourceEl.getAttribute("src")) ||
            "";

        const player = videojs(videoEl, {
            controls: true,
            muted: false,
            autoplay: false,
            preload: "auto",
            playsinline: true,
            bigPlayButton: true,
            controlBar: {
                children: ["playToggle", "fullscreenToggle"],
            },
        });

        if (existingSrc) {
            player.src({ type: "video/mp4", src: existingSrc });
        }

        player.on("loadedmetadata", function () {
            const isPortrait = player.videoHeight() > player.videoWidth();
            const isLandscape = player.videoWidth() > player.videoHeight();

            $(elms.classIntroVideo).removeClass("is-portrait is-landscape");
            if (isPortrait) {
                $(elms.classIntroVideo).addClass("is-portrait");
            }
            if (isLandscape) {
                $(elms.classIntroVideo).addClass("is-landscape");
            }
        });

        player.on("fullscreenchange", function () {
            if (player.isFullscreen()) {
                $(elms.classIntroVideo).addClass("is-fullscreen");
                if (
                    screen.orientation &&
                    // @ts-ignore
                    screen.orientation.lock
                ) {
                    const isPortrait =
                        player.videoHeight() > player.videoWidth();

                    if (isPortrait) {
                        screen.orientation
                            // @ts-ignore
                            .lock("portrait")
                            .catch((err) => {
                                console.warn("Orientation lock failed:", err);
                            });
                    } else {
                        screen.orientation
                            // @ts-ignore
                            .lock("landscape")
                            .catch((err) => {
                                console.warn("Orientation lock failed:", err);
                            });
                    }
                }
            } else {
                $(elms.classIntroVideo).removeClass("is-fullscreen");
                if (
                    screen.orientation &&
                    // @ts-ignore
                    screen.orientation.unlock
                ) {
                    screen.orientation.unlock();
                }
            }
        });

        player.on("ended", closeIntro);
    };

    const stopScanner = () => {
        scanning = false;
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            stream = null;
        }
        const video = /** @type {HTMLVideoElement} */ ($(elms.qrVideo)[0]);
        if (video) {
            video.srcObject = null;
            video.style.display = "none";
        }
    };

    const initQRScanner = () => {
        const video = $(elms.qrVideo)[0];
        const canvas = $(elms.qrCanvas)[0];
        if (!(video instanceof HTMLVideoElement)) return;
        if (!(canvas instanceof HTMLCanvasElement)) return;

        if (!video || !canvas) {
            console.error("Required elements not found");
            return;
        }

        const startScanner = async () => {
            try {
                const constraints = {
                    video: {
                        facingMode: "environment",
                        width: { ideal: 1280, min: 640 },
                        height: { ideal: 720, min: 480 },
                        aspectRatio: { ideal: 16 / 9 },
                        frameRate: { ideal: 30, min: 15 },
                    },
                };

                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(
                    (device) => device.kind === "videoinput",
                );

                if (videoDevices.length > 1) {
                    const backCamera = videoDevices.find(
                        (device) =>
                            device.label.toLowerCase().includes("back") ||
                            device.label.toLowerCase().includes("rear") ||
                            device.label.toLowerCase().includes("environment"),
                    );

                    if (backCamera) {
                        constraints.video.deviceId = {
                            exact: backCamera.deviceId,
                        };
                    }
                }

                stream = await navigator.mediaDevices.getUserMedia(constraints);

                video.srcObject = stream;
                video.style.display = "block";
                video.setAttribute("playsinline", "true");
                video.setAttribute("webkit-playsinline", "true");
                video.muted = true;
                video.autoplay = true;

                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error("Video loading timeout"));
                    }, 10000);

                    video.onloadedmetadata = () => {
                        clearTimeout(timeout);
                        resolve();
                    };

                    video.onerror = () => {
                        clearTimeout(timeout);
                        reject(new Error("Video loading failed"));
                    };
                });

                await video.play();
                scanning = true;

                setTimeout(() => {
                    scanQRCode();
                }, 500);
            } catch (error) {
                console.error("Error accessing camera:", error);

                try {
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: "environment",
                        },
                    });

                    video.srcObject = stream;
                    video.style.display = "block";
                    video.setAttribute("playsinline", "true");
                    video.setAttribute("webkit-playsinline", "true");
                    video.muted = true;
                    video.autoplay = true;

                    await video.play();
                    scanning = true;

                    setTimeout(() => {
                        scanQRCode();
                    }, 500);
                } catch (fallbackError) {
                    console.error(
                        "Fallback camera access also failed:",
                        fallbackError,
                    );
                    stopScanner();

                    alert(
                        "Camera access denied. Please allow camera permissions and refresh the page.",
                    );
                }
            }
        };

        const loadQuestion = async (qrCode) => {
            const id = qrCode.split("=")[1];

            const [response] = await bdjs.goAsync(
                bdjs.makeRequest(
                    window.location.origin + "/wp-admin/admin-ajax.php",
                    {
                        action: "get_question",
                        question_id: id,
                        nonce: CFG.nonce,
                        current_language: CFG.current_language,
                    },
                ),
            );

            if (response.data.success) {
                const questionData = response.data.data;
                if (Array.isArray(questionData) && questionData.length === 0) {
                    const alertText = $(elms.alertText).html();
                    alert(alertText);
                    stopScanner();
                    setTimeout(() => {
                        startScannerIfAllowed();
                    }, 1000);
                    return;
                }

                if (
                    questionData.question_type &&
                    questionData.question_type.length > 0
                ) {
                    displayQuestion(questionData, isIOS);
                }

                if (
                    questionData.answer_type &&
                    questionData.answer_type.length > 0
                ) {
                    displayAnswer(questionData, isIOS);
                }
            }
        };

        const displayQuestion = (questionData, isIOS) => {
            const questionType = questionData.question_type[0];
            const layout = questionType.acf_fc_layout;
            const questionContainer = $(elms.question);

            switch (layout) {
                case "video": {
                    const videoSource = questionType.video || false;
                    if (videoSource) {
                        if (isIOS) {
                            $(elms.classQuestionVideoIOS).show();
                            $(elms.questionVideoNormal).attr(
                                "src",
                                videoSource,
                            );
                            $(elms.questionVideoFullscreen).attr(
                                "src",
                                videoSource,
                            );

                            const checkVideoOrientation = () => {
                                const normalVideo =
                                    /** @type {HTMLVideoElement} */ (
                                        $(elms.questionVideoNormal)[0]
                                    );

                                if (
                                    normalVideo.videoWidth &&
                                    normalVideo.videoHeight
                                ) {
                                    const isPortrait =
                                        normalVideo.videoHeight >
                                        normalVideo.videoWidth;
                                    const isLandscape =
                                        normalVideo.videoWidth >
                                        normalVideo.videoHeight;

                                    if (isPortrait) {
                                        $(elms.classQuestionVideoIOS).addClass(
                                            "is-portrait",
                                        );
                                        $(
                                            elms.classQuestionVideoIOS,
                                        ).removeClass("is-landscape");
                                    } else {
                                        $(elms.classQuestionVideoIOS).addClass(
                                            "is-landscape",
                                        );
                                        $(
                                            elms.classQuestionVideoIOS,
                                        ).removeClass("is-portrait");
                                    }

                                    return { isPortrait, isLandscape };
                                }
                                return null;
                            };

                            $(elms.questionVideoNormal).on(
                                "loadedmetadata",
                                checkVideoOrientation,
                            );
                            $(elms.questionVideoFullscreen).on(
                                "loadedmetadata",
                                checkVideoOrientation,
                            );

                            $(elms.questionVideoNormal).on(
                                "ended",
                                onVideoEnded,
                            );
                            $(elms.questionVideoFullscreen).on(
                                "ended",
                                onVideoEnded,
                            );
                        } else {
                            $(elms.classQuestionVideo).show();

                            // Dispose of existing player if it exists
                            if (videojs.getPlayer(elms.questionVideo)) {
                                videojs(elms.questionVideo).dispose();
                            }

                            // Initialize videojs first, then set the source
                            const player = videojs(elms.questionVideo, {
                                controls: true,
                                muted: false,
                                autoplay: false,
                                preload: "auto",
                                bigPlayButton: true,
                                controlBar: {
                                    children: [
                                        "playToggle",
                                        "fullscreenToggle",
                                        // "progressControl",
                                    ],
                                },
                            });

                            player.src(videoSource);

                            player.on("ended", onVideoEnded);

                            const forcePortrait = async () => {
                                try {
                                    // @ts-ignore
                                    if (screen.orientation?.lock) {
                                        // @ts-ignore
                                        await screen.orientation.lock(
                                            "portrait",
                                        );
                                    }
                                } catch (err) {
                                    console.warn("Portrait lock failed:", err);
                                }
                            };

                            player.on("fullscreenchange", function () {
                                if (player.isFullscreen()) {
                                    if (
                                        screen.orientation &&
                                        // @ts-ignore
                                        screen.orientation.lock
                                    ) {
                                        const isPortrait =
                                            player.videoHeight() >
                                            player.videoWidth();

                                        if (isPortrait) {
                                            screen.orientation
                                                // @ts-ignore
                                                .lock("portrait")
                                                .catch((err) => {
                                                    console.warn(
                                                        "Orientation lock failed:",
                                                        err,
                                                    );
                                                });

                                            $(elms.classQuestionVideo).addClass(
                                                "is-fullscreen",
                                            );
                                        } else {
                                            screen.orientation
                                                // @ts-ignore
                                                .lock("landscape")
                                                .catch((err) => {
                                                    console.warn(
                                                        "Orientation lock failed:",
                                                        err,
                                                    );
                                                });
                                        }
                                    }
                                } else {
                                    const isLandscape =
                                        window.innerWidth > window.innerHeight;
                                    if (isLandscape) {
                                        $(elms.questionWarning).show();
                                    }

                                    $(elms.classQuestionVideo).removeClass(
                                        "is-fullscreen",
                                    );

                                    if (
                                        screen.orientation &&
                                        // @ts-ignore
                                        screen.orientation.unlock
                                    ) {
                                        screen.orientation.unlock();
                                    }

                                    if (
                                        screen.orientation &&
                                        // @ts-ignore
                                        screen.orientation.lock
                                    ) {
                                        forcePortrait();
                                    }
                                }
                            });

                            player.on("loadedmetadata", function () {
                                const isPortrait =
                                    player.videoHeight() > player.videoWidth();
                                const isLandscape =
                                    player.videoWidth() > player.videoHeight();

                                if (isPortrait) {
                                    $(elms.classQuestionVideo).addClass(
                                        "is-portrait",
                                    );
                                }

                                if (isLandscape) {
                                    $(elms.classQuestionVideo).addClass(
                                        "is-landscape",
                                    );
                                }
                            });

                            $(elms.classQuestionFullScreen).on(
                                "click",
                                function () {
                                    if (player.isFullscreen()) {
                                        player.exitFullscreen();
                                    } else {
                                        player.requestFullscreen();
                                    }
                                },
                            );
                        }

                        $(elms.classQuestionFullScreen).show();
                    } else {
                        $(elms.classQuestionInner).show();
                        $(elms.classQuestionInner).html(
                            "<div class='question__inner'>Video source not available</div>",
                        );
                    }
                    break;
                }

                case "image": {
                    const imageSource = questionType.image || false;
                    if (imageSource) {
                        $(elms.classQuestionImage).show();
                        $(elms.showAnswerQuestion).show();
                        $(elms.classQuestionImage).attr("src", imageSource);
                        $(elms.classQuestionFullScreen)
                            .show()
                            .addClass("is-image");
                    } else {
                        $(elms.classQuestionInner).show();
                        $(elms.classQuestionInner).html(
                            "<div class='question__inner'>Image source not available</div>",
                        );
                    }
                    break;
                }

                case "audio_image": {
                    const audioSource = questionType.audio || false;
                    const audioImageSource = questionType.image || false;

                    if (audioSource && audioImageSource) {
                        $(elms.classQuestionAudio).show();
                        $(elms.classQuestionImage).show();
                        $(elms.questionAudioContainer).show();
                        $(elms.classQuestionFullScreen)
                            .show()
                            .addClass("is-image");
                        $(elms.questionAudio).attr("src", audioSource);
                        $(elms.classQuestionImage).attr(
                            "src",
                            audioImageSource,
                        );
                    } else if (audioSource) {
                        $(elms.classQuestionAudio).show();
                        $(elms.questionAudioContainer).show();

                        $(elms.questionAudio).attr("src", audioSource);
                    } else if (audioImageSource) {
                        $(elms.classQuestionImage).show();
                        $(elms.classQuestionFullScreen)
                            .show()
                            .addClass("is-image");
                        $(elms.classQuestionImage).attr(
                            "src",
                            audioImageSource,
                        );
                    } else {
                        $(elms.classQuestionInner).show();
                        $(elms.classQuestionInner).html(
                            "<div class='question__inner'>Audio or image source not available</div>",
                        );
                    }
                    break;
                }

                case "text": {
                    const textContent = questionType.text || false;
                    if (textContent) {
                        $(elms.classQuestionInner).show();
                        $(elms.showAnswerQuestion).show();
                        $(elms.classQuestionInner).html(
                            `<div class="question__inner">${textContent}</div>`,
                        );
                    } else {
                        $(elms.classQuestionInner).show();
                        $(elms.classQuestionInner).html(
                            "<div class='question__inner'>Text content not available</div>",
                        );
                    }
                    break;
                }

                default:
                    $(elms.classQuestionInner).show();
                    $(elms.classQuestionInner).html(
                        "<div class='question__inner'>Unknown question type</div>",
                    );
                    break;
            }

            if (layout !== "text") {
                const clueText = questionType.question || "";
                $(elms.questionClue).html(clueText).show();
            } else {
                $(elms.questionClue).hide().empty();
            }

            questionContainer.addClass("is-open");

            if (layout === "video" && questionType.video) {
                const videoElement = /** @type {HTMLVideoElement} */ (
                    questionContainer.find(elms.questionVideo)[0]
                );

                if (videoElement) {
                    videoElement.addEventListener("ended", onVideoEnded);
                }

                $(elms.classQuestionFullScreen).show();
            }

            if (layout === "audio_image" && questionType.audio) {
                const audioElement = /** @type {HTMLAudioElement} */ (
                    questionContainer.find(elms.questionAudio)[0]
                );
                if (audioElement) {
                    audioElement.addEventListener("ended", onAudioEnded);
                }
            }
        };

        const displayAnswer = (questionData, isIOS) => {
            $(elms.answerYear).html(questionData.answer_event_year);
            $(elms.answerTitle).html(questionData.answer_event_title);
            $(elms.answerDescription).html(
                questionData.answer_event_description,
            );
            $(elms.answerCategory).html(
                questionData.category?.category_name || "",
            );

            const categoryBackgroundImage =
                questionData.category?.category_background_image || false;
            if (categoryBackgroundImage) {
                $(elms.answerBackground).attr("src", categoryBackgroundImage);
            }

            const categoryColor =
                questionData.category?.category_color || "#d70073";
            const categoryColorWithTransparency = categoryColor + "80";
            $(elms.answerSubtitle).css("color", categoryColorWithTransparency);
            $(elms.answerTitle).css("color", categoryColor);
            $(elms.answerYear).css("color", categoryColor);
            $(elms.answerCategory).css("background-color", categoryColor);
            $(elms.answerAudioContainer).find("p").css("color", categoryColor);
            $(elms.answerAudioContainer)
                .find("span")
                .css("color", categoryColor);

            $(elms.answerAudioText)
                .find("span")
                .css("background-color", categoryColor);
            $(elms.answerAudioContainer)
                .find("svg path")
                .css("fill", categoryColor);

            const answerType = questionData.answer_type[0];
            const layout = answerType.acf_fc_layout;

            switch (layout) {
                case "video": {
                    const videoSource = answerType.video || false;
                    if (videoSource) {
                        if (isIOS) {
                            $(elms.answerVideoContainerIOS).show();
                            $(elms.answerVideoNormal).attr("src", videoSource);
                            $(elms.answerVideoFullscreen).attr(
                                "src",
                                videoSource,
                            );

                            const checkVideoOrientation = () => {
                                const normalVideo =
                                    /** @type {HTMLVideoElement} */ (
                                        $(elms.answerVideoNormal)[0]
                                    );

                                if (
                                    normalVideo.videoWidth &&
                                    normalVideo.videoHeight
                                ) {
                                    const isPortrait =
                                        normalVideo.videoHeight >
                                        normalVideo.videoWidth;
                                    const isLandscape =
                                        normalVideo.videoWidth >
                                        normalVideo.videoHeight;

                                    if (isPortrait) {
                                        $(
                                            elms.answerVideoContainerIOS,
                                        ).addClass("is-portrait");
                                        $(
                                            elms.answerVideoContainerIOS,
                                        ).removeClass("is-landscape");
                                    } else {
                                        $(
                                            elms.answerVideoContainerIOS,
                                        ).addClass("is-landscape");
                                        $(
                                            elms.answerVideoContainerIOS,
                                        ).removeClass("is-portrait");
                                    }

                                    return { isPortrait, isLandscape };
                                }
                                return null;
                            };

                            $(elms.answerVideoNormal).on(
                                "loadedmetadata",
                                checkVideoOrientation,
                            );
                            $(elms.answerVideoFullscreen).on(
                                "loadedmetadata",
                                checkVideoOrientation,
                            );

                            $(elms.answerVideoNormal).on(
                                "ended",
                                onVideoAnswerEnded,
                            );
                            $(elms.answerVideoFullscreen).on(
                                "ended",
                                onVideoAnswerEnded,
                            );
                        } else {
                            $(elms.answerVideoContainer).show();
                            $(elms.answerVideo).attr("src", videoSource);

                            // Dispose of existing player if it exists
                            if (videojs.getPlayer(elms.answerVideo)) {
                                videojs(elms.answerVideo).dispose();
                            }

                            // Initialize videojs first, then set the source
                            const player = videojs(elms.answerVideo, {
                                controls: true,
                                muted: false,
                                autoplay: false,
                                preload: "auto",
                                bigPlayButton: true,
                                controlBar: {
                                    children: [
                                        "playToggle",
                                        "fullscreenToggle",
                                        // "progressControl",
                                    ],
                                },
                            });

                            // Set the source after videojs is initialized
                            player.src(videoSource);

                            player.on("ended", onVideoAnswerEnded);

                            player.on("fullscreenchange", function () {
                                if (player.isFullscreen()) {
                                    if (
                                        screen.orientation &&
                                        // @ts-ignore
                                        screen.orientation.lock
                                    ) {
                                        const isPortrait =
                                            player.videoHeight() >
                                            player.videoWidth();

                                        if (isPortrait) {
                                            screen.orientation
                                                // @ts-ignore
                                                .lock("portrait")
                                                .catch((err) => {
                                                    console.warn(
                                                        "Orientation lock failed:",
                                                        err,
                                                    );
                                                });
                                        } else {
                                            screen.orientation
                                                // @ts-ignore
                                                .lock("landscape")
                                                .catch((err) => {
                                                    console.warn(
                                                        "Orientation lock failed:",
                                                        err,
                                                    );
                                                });
                                        }
                                    }
                                } else {
                                    const isLandscape =
                                        window.innerWidth > window.innerHeight;
                                    if (isLandscape) {
                                        $(elms.answerWarning).show();
                                    }
                                    if (
                                        screen.orientation &&
                                        screen.orientation.unlock
                                    ) {
                                        screen.orientation.unlock();
                                    }
                                }
                            });

                            player.on("loadedmetadata", function () {
                                const isPortrait =
                                    player.videoHeight() > player.videoWidth();
                                const isLandscape =
                                    player.videoWidth() > player.videoHeight();

                                if (isPortrait) {
                                    $(elms.answerVideoContainer).addClass(
                                        "is-portrait",
                                    );
                                }

                                if (isLandscape) {
                                    $(elms.answerVideoContainer).addClass(
                                        "is-landscape",
                                    );
                                }
                            });

                            $(elms.classAnswerFullScreen).on(
                                "click",
                                function () {
                                    if (player.isFullscreen()) {
                                        player.exitFullscreen();
                                    } else {
                                        player.requestFullscreen();
                                    }
                                },
                            );

                            $(elms.classAnswerFullScreen).show();
                        }
                    } else {
                        $(elms.answerText).html("Video source not available");
                    }
                    break;
                }

                case "audio_image": {
                    const audioSource = answerType.audio || false;
                    const imageSource = answerType.image || false;

                    if (audioSource && imageSource) {
                        $(elms.answerAudioContainer).show();
                        $(elms.answerAudio).show();
                        $(elms.answerAudio).attr("src", answerType.audio);
                        $(elms.answerImageContainer).show();
                        $(elms.answerImage).attr("src", answerType.image);
                        $(elms.classAnswerFullScreen).show();
                    } else if (audioSource) {
                        $(elms.answerAudioContainer).show();
                        $(elms.answerAudio).show();
                        $(elms.answerAudio).attr("src", answerType.audio);
                    } else if (imageSource) {
                        $(elms.answerImageContainer).show();
                        $(elms.answerImage).attr("src", answerType.image);
                        $(elms.classAnswerFullScreen).show();
                    } else {
                        $(elms.answerText).html(
                            "Audio or image source not available",
                        );
                    }
                    break;
                }

                case "image": {
                    const imageSource = answerType.image || false;
                    if (imageSource) {
                        $(elms.answerImageContainer).show();
                        $(elms.nextQuestion).prop("disabled", false);
                        $(elms.answerImage).attr("src", imageSource);
                        $(elms.classAnswerFullScreen).show();
                    } else {
                        $(elms.answerText).html("Image source not available");
                    }
                    break;
                }

                case "text": {
                    const textContent = answerType.text || false;
                    if (textContent) {
                        $(elms.nextQuestion).prop("disabled", false);
                        $(elms.answerDescription).html(textContent);
                    } else {
                        $(elms.answerText).html("Text content not available");
                    }
                    break;
                }

                default: {
                    $(elms.answerText).html("Unknown answer type");
                    break;
                }
            }
        };

        scanQRCode = () => {
            if (!scanning) return;

            try {
                // Check if video is ready and has valid dimensions
                if (!video.videoWidth || !video.videoHeight) {
                    requestAnimationFrame(scanQRCode);
                    return;
                }

                const context = canvas.getContext("2d");

                // Use actual video dimensions for better iPhone X compatibility
                const videoWidth = video.videoWidth;
                const videoHeight = video.videoHeight;

                // Set canvas size to match video dimensions
                canvas.width = videoWidth;
                canvas.height = videoHeight;

                // Clear canvas before drawing
                context.clearRect(0, 0, canvas.width, canvas.height);

                // Draw video frame to canvas
                context.drawImage(video, 0, 0, canvas.width, canvas.height);

                const imageData = context.getImageData(
                    0,
                    0,
                    canvas.width,
                    canvas.height,
                );
                const code = jsQR(
                    imageData.data,
                    imageData.width,
                    imageData.height,
                    {
                        inversionAttempts: "dontInvert",
                    },
                );

                if (code) {
                    scanning = false;
                    loadQuestion(code.data);
                } else {
                    requestAnimationFrame(scanQRCode);
                }
            } catch (error) {
                console.error("Error scanning QR code:", error);
                requestAnimationFrame(scanQRCode);
            }
        };

        startScanner();
        // debug, remove when done
        // setTimeout(function () {
        //     loadQuestion("https://fci-all-stars.local/?id=333");
        // }, 1000);
    };

    const onVideoEnded = () => {
        $(elms.app).addClass("is-ended");
        $(elms.answer).addClass("is-open");
        $(elms.playVideo).removeClass("is-playing");

        if (isIOS) {
            $(elms.classQuestionVideoIOS).removeClass("is-fullscreen");
        } else {
            const player = videojs.getPlayer(elms.questionVideo);
            if (player && player.isFullscreen && player.isFullscreen()) {
                player.exitFullscreen();
            }
        }
    };

    const onAudioEnded = () => {
        $(elms.app).addClass("is-ended");
        $(elms.answer).addClass("is-open");
        $(elms.questionAudioText).removeClass("is-playing");
        $(elms.questionAudioText).addClass("is-finished");
    };

    const onVideoAnswerEnded = () => {
        $(elms.nextQuestion).prop("disabled", false);
        $(elms.playAnswerVideo).removeClass("is-playing");

        if (isIOS) {
            $(elms.answerVideoContainerIOS).addClass("is-finished");
        } else {
            $(elms.answerVideoContainer).addClass("is-finished");
            const player = videojs.getPlayer(elms.answerVideo);
            if (player && player.isFullscreen && player.isFullscreen()) {
                player.exitFullscreen();
            }
        }
    };

    const onAudioAnswerEnded = () => {
        $(elms.answerAudioContainer).removeClass("is-playing");
        $(elms.answerAudioContainer).addClass("is-finished");
        $(elms.answerAudioReplay).prop("disabled", false);
        $(elms.nextQuestion).prop("disabled", false);
    };

    const replayVideo = () => {
        $(elms.app).removeClass("is-ended");
        $(elms.answer).removeClass("is-open");

        if (isIOS) {
            const videoElement = /** @type {HTMLVideoElement} */ (
                $(elms.questionVideoNormal)[0]
            );
            if (videoElement) {
                $(elms.playVideo).addClass("is-playing");
                videoElement.play();
            }
        } else {
            if (!videojs.getPlayer(elms.questionVideo)) return;
            videojs(elms.questionVideo).play();
        }
    };

    const replayAudio = () => {
        if ($(elms.questionAudio).length === 0) return;

        $(elms.app).removeClass("is-ended");
        $(elms.answer).removeClass("is-open");

        const audioElement = /** @type {HTMLAudioElement} */ (
            $(elms.questionAudio)[0]
        );
        if (audioElement.src !== homeURL && audioElement.src !== "") {
            $(elms.questionAudioText).addClass("is-playing");
            $(elms.questionAudioText).removeClass("is-finished");

            audioElement.play().catch((error) => {
                console.warn("Audio play failed:", error);
            });
        }
    };

    const playQuestionAudio = () => {
        if ($(elms.questionAudio).length === 0) return;

        const audioElement = /** @type {HTMLAudioElement} */ (
            $(elms.questionAudio)[0]
        );
        if (audioElement.src !== homeURL && audioElement.src !== "") {
            audioElement
                .play()
                .then(() => {
                    $(elms.questionAudioContainer).hide();
                    $(elms.questionAudioText).addClass("is-playing");
                    $(elms.questionAudioText).removeClass("is-finished");
                })
                .catch((error) => {
                    console.warn("Answer audio play failed:", error);
                });
        }
    };

    const replayAnswerVideo = () => {
        $(elms.nextQuestion).prop("disabled", true);

        if (isIOS) {
            const videoElement = /** @type {HTMLVideoElement} */ (
                $(elms.answerVideoNormal)[0]
            );
            if (videoElement) {
                videoElement.play();
            }
        } else {
            if (!videojs.getPlayer(elms.answerVideo)) return;

            $(elms.answerVideoContainer).removeClass("is-finished");
            videojs(elms.answerVideo).play();
        }
    };

    const replayAnswerAudio = () => {
        if ($(elms.answerAudio).length === 0) return;

        const audioElement = /** @type {HTMLAudioElement} */ (
            $(elms.answerAudio)[0]
        );
        if (audioElement.src !== homeURL && audioElement.src !== "") {
            audioElement
                .play()
                .then(() => {
                    $(elms.answerAudioContainer).removeClass("is-finished");
                    $(elms.answerAudioContainer).addClass("is-playing");
                    $(elms.answerAudioReplay).prop("disabled", true);
                    $(elms.nextQuestion).prop("disabled", true);
                })
                .catch((error) => {
                    console.warn("Answer audio play failed:", error);
                });
        }
    };

    const showAnswer = () => {
        $(elms.answer).addClass("show-answer");
    };

    const showAnswerQuestion = () => {
        $(elms.answer).addClass("is-open show-answer text-image");
    };

    const nextQuestion = () => {
        bdjs.showLoading();
        const pathSegments = window.location.pathname
            .split("/")
            .filter((segment) => segment);

        const firstPath = pathSegments.length > 0 ? `/${pathSegments[0]}` : "";
        window.location.replace(window.location.origin + firstPath);
    };

    const openFullscreenVideo = () => {
        const isFullscreen = $(elms.classQuestionVideoIOS).hasClass(
            "is-fullscreen",
        );

        const normalVideo = /** @type {HTMLVideoElement} */ (
            $(elms.questionVideoNormal)[0]
        );

        const fullscreenVideo = /** @type {HTMLVideoElement} */ (
            $(elms.questionVideoFullscreen)[0]
        );

        const isPortrait =
            fullscreenVideo.videoHeight > fullscreenVideo.videoWidth;

        if (isFullscreen) {
            const currentTime = fullscreenVideo.currentTime;

            const isPlaying = !fullscreenVideo.paused;

            fullscreenVideo.pause();
            $(elms.classQuestionVideoIOS).removeClass("is-fullscreen");

            normalVideo.currentTime = currentTime;

            if (isPlaying) normalVideo.play();
        } else {
            const currentTime = normalVideo.currentTime;

            const isPlaying = !normalVideo.paused;

            normalVideo.pause();
            $(elms.classQuestionVideoIOS).addClass("is-fullscreen");

            if (isPortrait) {
                $(elms.classQuestionVideoIOS).addClass("is-portrait");
            }

            fullscreenVideo.currentTime = currentTime;

            if (isPlaying) fullscreenVideo.play();
        }
    };

    const openFullscreenAnswerVideo = () => {
        const isFullscreen = $(elms.classAnswerVideo).hasClass("is-fullscreen");

        const normalVideo = /** @type {HTMLVideoElement} */ (
            $(elms.answerVideoNormal)[0]
        );

        const fullscreenVideo = /** @type {HTMLVideoElement} */ (
            $(elms.answerVideoFullscreen)[0]
        );

        const isPortrait =
            fullscreenVideo.videoHeight > fullscreenVideo.videoWidth;

        if (isFullscreen) {
            const currentTime = fullscreenVideo.currentTime;

            const isPlaying = !fullscreenVideo.paused;

            fullscreenVideo.pause();
            $(elms.classAnswerVideo).removeClass("is-fullscreen");

            normalVideo.currentTime = currentTime;

            if (isPlaying) normalVideo.play();
        } else {
            const currentTime = normalVideo.currentTime;

            const isPlaying = !normalVideo.paused;

            normalVideo.pause();
            $(elms.classAnswerVideo).addClass("is-fullscreen");

            if (isPortrait) {
                $(elms.classAnswerVideo).addClass("is-portrait");
            }

            fullscreenVideo.currentTime = currentTime;

            if (isPlaying) fullscreenVideo.play();
        }
    };

    /**
     * Intro fullscreen from .intro__text button (like question .question__text).
     * iOS: custom fullscreen toggle. Non-iOS: Video.js requestFullscreen/exitFullscreen.
     */
    const triggerIntroFullscreen = () => {
        if (isIOS) {
            openFullscreenIntroVideo();
            return;
        } else {
            const videoEl = document.querySelector(elms.introVideo);
            const player = videoEl ? videojs.getPlayer(videoEl) : null;
            if (!player) return;
            if (player.isFullscreen()) {
                player.exitFullscreen();
            } else {
                player.requestFullscreen();
            }
        }
    };

    /**
     * iOS intro video: play/pause (separate from question playVideo).
     */
    const playIntroVideo = (e) => {
        const isFullscreen = $(elms.classIntroVideoIOS).hasClass(
            "is-fullscreen",
        );

        const videoElement = isFullscreen
            ? /** @type {HTMLVideoElement} */ ($(elms.introVideoFullscreen)[0])
            : /** @type {HTMLVideoElement} */ ($(elms.introVideoNormal)[0]);

        if (!videoElement) return;
        e.preventDefault();

        if (videoElement.paused) {
            videoElement.play();
            $(e.currentTarget).addClass("is-playing");
        } else {
            videoElement.pause();
            $(e.currentTarget).removeClass("is-playing");
        }
    };

    /**
     * iOS intro video: toggle fullscreen (separate from question openFullscreenVideo).
     */
    const openFullscreenIntroVideo = () => {
        if (isIOS) {
            $(elms.classIntroVideoIOS).removeAttr("style");
        }

        const isFullscreen = $(elms.classIntroVideoIOS).hasClass(
            "is-fullscreen",
        );

        const normalVideo = /** @type {HTMLVideoElement} */ (
            $(elms.introVideoNormal)[0]
        );
        const fullscreenVideo = /** @type {HTMLVideoElement} */ (
            $(elms.introVideoFullscreen)[0]
        );

        if (!normalVideo || !fullscreenVideo) return;

        const isPortrait =
            fullscreenVideo.videoHeight > fullscreenVideo.videoWidth;

        if (isFullscreen) {
            const currentTime = fullscreenVideo.currentTime;
            const isPlaying = !fullscreenVideo.paused;

            fullscreenVideo.pause();
            $(elms.classIntroVideoIOS).removeClass("is-fullscreen");

            $(elms.introFullscreenText).show();

            normalVideo.currentTime = currentTime;
            if (isPlaying) normalVideo.play();
        } else {
            const currentTime = normalVideo.currentTime;
            const isPlaying = !normalVideo.paused;

            normalVideo.pause();
            $(elms.classIntroVideoIOS).addClass("is-fullscreen");

            $(elms.introFullscreenText).hide();

            if (isPortrait) {
                $(elms.classIntroVideoIOS).addClass("is-portrait");
            }

            fullscreenVideo.currentTime = currentTime;
            if (isPlaying) fullscreenVideo.play();
        }
    };

    const playVideo = (e) => {
        const isFullscreen = $(elms.classQuestionVideoIOS).hasClass(
            "is-fullscreen",
        );

        const videoElement = isFullscreen
            ? /** @type {HTMLVideoElement} */ (
                  $(elms.questionVideoFullscreen)[0]
              )
            : /** @type {HTMLVideoElement} */ ($(elms.questionVideoNormal)[0]);

        if (!videoElement) return;
        e.preventDefault();

        if (videoElement.paused) {
            videoElement.play();

            $(e.currentTarget).addClass("is-playing");
        } else {
            videoElement.pause();

            $(e.currentTarget).removeClass("is-playing");
        }
    };

    const playAnswerVideo = (e) => {
        const isFullscreen = $(elms.classAnswerVideo).hasClass("is-fullscreen");

        const videoElement = isFullscreen
            ? /** @type {HTMLVideoElement} */ ($(elms.answerVideoFullscreen)[0])
            : /** @type {HTMLVideoElement} */ ($(elms.answerVideoNormal)[0]);

        if (!videoElement) return;
        e.preventDefault();

        $(elms.nextQuestion).prop("disabled", true);

        if (videoElement.paused) {
            videoElement.play();

            $(e.currentTarget).addClass("is-playing");
        } else {
            videoElement.pause();

            $(e.currentTarget).removeClass("is-playing");
        }
    };

    $(document).ready(() => {
        // Show intro once per login session (sessionStorage); cleared on login
        const $intro = $(elms.intro);
        let shouldDelayScannerStart = false;
        if ($intro.length) {
            try {
                localStorage.removeItem(INTRO_WATCHED_LEGACY_KEY);
            } catch (e) {
                // ignore
            }
            try {
                if (!sessionStorage.getItem(INTRO_WATCHED_SESSION_KEY)) {
                    $intro.addClass("is-open");
                    shouldDelayScannerStart = true;
                }
            } catch (e) {
                $intro.addClass("is-open");
                shouldDelayScannerStart = true;
            }
        }

        if (isIOS) {
            initIntroVideoIOS();
            const $introIos = $(elms.classIntroVideoIOS);
            if ($introIos.length) {
                $introIos.removeAttr("style");
                $(elms.classIntroVideo).hide();
            }
        } else {
            initIntroVideo();
            $(elms.classIntroVideoIOS).hide();
        }
        if (!shouldDelayScannerStart) {
            startScannerIfAllowed();
        }

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                if ($(elms.answer).hasClass("is-open")) {
                    if ($(elms.answerVideo).length) {
                        if (isIOS) {
                            const videoElement =
                                /** @type {HTMLVideoElement} */ (
                                    $(elms.answerVideoNormal)[0]
                                );
                            if (videoElement) {
                                videoElement.pause();
                            }
                        } else {
                            const answerPlayer = videojs.getPlayer(
                                elms.answerVideo,
                            );
                            if (answerPlayer && !answerPlayer.paused()) {
                                answerPlayer.pause();
                            }
                        }
                    }
                    if ($(elms.answerAudio).length) {
                        const answerAudioEl = /** @type {HTMLAudioElement} */ (
                            $(elms.answerAudio)[0]
                        );
                        if (!answerAudioEl.paused) {
                            answerAudioEl.pause();
                        }
                    }
                } else if ($(elms.question).hasClass("is-open")) {
                    if ($(elms.questionVideo).length) {
                        if (isIOS) {
                            const videoElement =
                                /** @type {HTMLVideoElement} */ (
                                    $(elms.questionVideoNormal)[0]
                                );
                            if (videoElement) {
                                videoElement.pause();
                            }
                        } else {
                            const player = videojs.getPlayer(
                                elms.questionVideo,
                            );
                            if (player && !player.paused()) {
                                player.pause();
                            }
                        }
                    }
                    if ($(elms.questionAudio).length) {
                        const audioEl = /** @type {HTMLAudioElement} */ (
                            $(elms.questionAudio)[0]
                        );
                        if (!audioEl.paused) {
                            audioEl.pause();
                        }
                    }
                } else {
                    stopScanner();
                }
            } else {
                if ($(elms.answer).hasClass("is-open")) {
                    if ($(elms.answerVideo).length) {
                        if (isIOS) {
                            const videoElement =
                                /** @type {HTMLVideoElement} */ (
                                    $(elms.answerVideoNormal)[0]
                                );
                            if (videoElement) {
                                videoElement.play();
                            }
                        } else {
                            const answerPlayer = videojs.getPlayer(
                                elms.answerVideo,
                            );
                            if (answerPlayer && answerPlayer.paused()) {
                                answerPlayer.play();
                            }
                        }
                    }
                    if ($(elms.answerAudio).length) {
                        const answerAudioEl = /** @type {HTMLAudioElement} */ (
                            $(elms.answerAudio)[0]
                        );
                        if (
                            answerAudioEl.paused &&
                            answerAudioEl.src !== "" &&
                            answerAudioEl.src !== homeURL
                        ) {
                            answerAudioEl.play();
                        }
                    }
                } else if ($(elms.question).hasClass("is-open")) {
                    if ($(elms.questionVideo).length) {
                        if (isIOS) {
                            const videoElement =
                                /** @type {HTMLVideoElement} */ (
                                    $(elms.questionVideoNormal)[0]
                                );
                            if (videoElement) {
                                videoElement.play();
                            }
                        } else {
                            const player = videojs.getPlayer(
                                elms.questionVideo,
                            );
                            if (player && player.paused()) {
                                player.play();
                            }
                        }
                    }
                    if ($(elms.questionAudio).length) {
                        const audioEl = /** @type {HTMLAudioElement} */ (
                            $(elms.questionAudio)[0]
                        );
                        if (
                            audioEl.paused &&
                            audioEl.src !== "" &&
                            audioEl.src !== homeURL
                        ) {
                            audioEl.play();
                        }
                    }
                } else {
                    setTimeout(() => {
                        startScannerIfAllowed();
                    }, 500);
                }
            }
        });
    });

    const openFullscreenImage = () => {
        const isFullscreen = $(elms.questionImageContainer).hasClass(
            "is-fullscreen",
        );

        if (isFullscreen) {
            $(elms.questionImageContainer).removeClass("is-fullscreen");
        } else {
            $(elms.questionImageContainer).addClass("is-fullscreen");
        }
    };

    const openFullscreenAnswerImage = () => {
        const isFullscreen = $(elms.answerImageContainer).hasClass(
            "is-fullscreen",
        );

        if (isFullscreen) {
            $(elms.answerImageContainer).removeClass("is-fullscreen");
        } else {
            $(elms.answerImageContainer).addClass("is-fullscreen");
        }
    };

    window.addEventListener("resize", () => {
        const isPortrait = window.innerWidth < window.innerHeight;
        if (isPortrait) {
            $(elms.questionWarning).hide();
            $(elms.answerWarning).hide();
        }
    });

    $(elms.questionAudio).on("ended", onAudioEnded);
    $(elms.answerAudio).on("ended", onAudioAnswerEnded);
    $(document.body).on("click", elms.replay, replayVideo);
    $(document.body).on("click", elms.replay, replayAudio);
    $(document.body).on("click", elms.questionAudioPlay, playQuestionAudio);
    $(document.body).on("click", elms.showAnswer, showAnswer);
    $(document.body).on("click", elms.showAnswerQuestion, showAnswerQuestion);
    $(document.body).on("click", elms.nextQuestion, nextQuestion);
    $(document.body).on("click", elms.answerReplay, replayAnswerVideo);
    $(document.body).on("click", elms.answerAudioReplay, replayAnswerAudio);
    $(document.body).on("click", elms.fullscreenVideo, openFullscreenVideo);
    $(document.body).on("click", elms.playVideo, playVideo);
    $(document.body).on("click", elms.skipIntroBtn, closeIntro);
    $(document.body).on("click", elms.introPlayVideo, playIntroVideo);
    $(document.body).on(
        "click",
        elms.introFullscreenVideo,
        triggerIntroFullscreen,
    );
    $(document.body).on(
        "click",
        elms.introFullscreenText,
        triggerIntroFullscreen,
    );
    $(document.body).on(
        "click",
        elms.classQuestionFullScreen,
        openFullscreenVideo,
    );
    $(document.body).on("click", elms.playAnswerVideo, playAnswerVideo);
    $(document.body).on(
        "click",
        elms.fullscreenAnswerVideo,
        openFullscreenAnswerVideo,
    );
    $(document.body).on(
        "click",
        elms.questionImageFullscreen,
        openFullscreenImage,
    );
    $(document.body).on(
        "click",
        elms.classQuestionFullScreen,
        openFullscreenImage,
    );
    $(document.body).on(
        "click",
        elms.classAnswerFullScreen,
        openFullscreenAnswerImage,
    );
    $(document.body).on(
        "click",
        elms.answerImageFullscreen,
        openFullscreenAnswerImage,
    );
};
